import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { IInboxApiTemplate, IInboxSettings, IInboxTemplateRequest } from "../models/messagesInbox/IInboxApi";
import { DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";

export const inboxSettingsKeys = {
  settings: ["messagesInbox", "settings"] as const,
  templates: ["messagesInbox", "templates"] as const,
};

/** What the inbox falls back to before the settings load, and for the sample data. */
export const DEFAULT_SIGN_OFF = "Kind regards,\n{shop_name}";
export const DEFAULT_REPLY_WINDOW_HOURS = 24;

async function unwrap<T>(request: Promise<DefaultResponseWithData<T>>): Promise<T> {
  const res = await request;
  if (!res.success) throw new Error(res.message || "Something went wrong – nothing was saved.");
  return res.data;
}

/**
 * The settings and templates behind the cog on the Messages page. Live only: on sample data nothing
 * is fetched and the defaults apply. Saving the AI and automatic-reply fields needs an admin; the
 * API says so (403) and the message is shown.
 */
export default function useMessagesInboxSettings(live: boolean, componentName = "useMessagesInboxSettings") {
  const { get, post, put, del } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  const settings = useQuery<IInboxSettings>(inboxSettingsKeys.settings, () => unwrap(get<DefaultResponseWithData<IInboxSettings>>("/messagesinbox/settings")), {
    enabled: live,
    staleTime: 5 * 60 * 1000,
  });

  const templates = useQuery<IInboxApiTemplate[]>(inboxSettingsKeys.templates, () => unwrap(get<DefaultResponseWithData<IInboxApiTemplate[]>>("/messagesinbox/templates")), {
    enabled: live,
    staleTime: 5 * 60 * 1000,
  });

  const saveSettings = useMutation((dto: IInboxSettings) => unwrap(put<IInboxSettings, DefaultResponseWithData<IInboxSettings>>("/messagesinbox/settings", dto)), {
    onSuccess: (saved) => queryClient.setQueryData(inboxSettingsKeys.settings, saved),
  });

  const refreshTemplates = () => queryClient.invalidateQueries(inboxSettingsKeys.templates);

  const addTemplate = useMutation((req: IInboxTemplateRequest) => unwrap(post<IInboxTemplateRequest, DefaultResponseWithData<IInboxApiTemplate>>("/messagesinbox/templates", req)), {
    onSuccess: refreshTemplates,
  });

  const updateTemplate = useMutation(
    ({ id, ...req }: IInboxTemplateRequest & { id: string }) =>
      unwrap(put<IInboxTemplateRequest, DefaultResponseWithData<IInboxApiTemplate>>(`/messagesinbox/templates/${encodeURIComponent(id)}`, req)),
    { onSuccess: refreshTemplates }
  );

  const deleteTemplate = useMutation((id: string) => unwrap(del<DefaultResponseWithData<boolean>>(`/messagesinbox/templates/${encodeURIComponent(id)}`)), {
    onSuccess: refreshTemplates,
  });

  return { settings, templates, saveSettings, addTemplate, updateTemplate, deleteTemplate };
}
